import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '../cn';

export const Select = RadixSelect.Root;
export const SelectValue = RadixSelect.Value;

export const SelectTrigger = forwardRef<
  HTMLButtonElement,
  RadixSelect.SelectTriggerProps & { className?: string }
>(({ children, className, ...props }, ref) => (
  <RadixSelect.Trigger
    ref={ref}
    className={cn(
      'inline-flex w-full items-center justify-between gap-2 h-10 px-3 text-base',
      'bg-surface text-fg-primary border border-border-default rounded-sm',
      'transition-colors duration-fast ease-in-out-cubic',
      'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30',
      'data-[placeholder]:text-fg-tertiary',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      className
    )}
    {...props}
  >
    {children}
    <RadixSelect.Icon asChild>
      <ChevronDown className="size-4 text-fg-secondary" />
    </RadixSelect.Icon>
  </RadixSelect.Trigger>
));
SelectTrigger.displayName = 'SelectTrigger';

export function SelectContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <RadixSelect.Portal>
      <RadixSelect.Content
        position="popper"
        sideOffset={4}
        className={cn(
          'z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-auto',
          'bg-surface-elev border border-border-default rounded-md p-1 shadow-lg',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          className
        )}
      >
        <RadixSelect.Viewport className="p-1">{children}</RadixSelect.Viewport>
      </RadixSelect.Content>
    </RadixSelect.Portal>
  );
}

export function SelectItem({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <RadixSelect.Item
      value={value}
      className={cn(
        'relative flex items-center gap-2 px-2 py-1.5 text-sm rounded-xs',
        'text-fg-primary cursor-default select-none',
        'data-[highlighted]:bg-surface data-[highlighted]:outline-none',
        'data-[state=checked]:font-medium',
        className
      )}
    >
      <span className="size-4 inline-flex items-center justify-center">
        <RadixSelect.ItemIndicator>
          <Check className="size-3.5 text-accent-fg" />
        </RadixSelect.ItemIndicator>
      </span>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  );
}
